import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { Project, ProjectApproval, ProjectApprovalLevel } from "../../../types/api";
import { getProjectApprovals, approveProject, rejectProject } from "../../../service/api";
import { CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { useAuth } from "../../../auth/AuthProvider";

interface ApprovalTimelineProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onApprovalChange?: () => void;
}

const ApprovalTimeline: React.FC<ApprovalTimelineProps> = ({
  isOpen,
  onClose,
  project,
  onApprovalChange,
}) => {
  const { user } = useAuth();
  const [approvalDoc, setApprovalDoc] = useState<ProjectApproval | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [showRemarksInput, setShowRemarksInput] = useState<Record<string, boolean>>({});

  const fetchApprovals = useCallback(async () => {
    if (!project) return;

    setLoading(true);
    try {
      const projectId = project.id || project._id;
      if (!projectId) return;

      const response = await getProjectApprovals(projectId);
      const data = response.data?.data || response.data?.message;
      setApprovalDoc(data || null);
    } catch (error: any) {
      toast.error("Failed to fetch approvals");
      console.error("Error fetching approvals:", error);
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (isOpen && project) {
      fetchApprovals();
    }
  }, [isOpen, project, fetchApprovals]);

  const handleApprove = async (level: ProjectApprovalLevel) => {
    if (!approvalDoc) return;
    const approvalId = approvalDoc.id || approvalDoc._id;
    if (!approvalId) return;

    const levelKey = `level_${level.level}`;
    setActionLoading({ ...actionLoading, [levelKey]: true });
    try {
      await approveProject(approvalId, level.level, remarks[levelKey] || "");
      toast.success("Approval submitted successfully");
      fetchApprovals();
      onApprovalChange?.();
      setShowRemarksInput({ ...showRemarksInput, [levelKey]: false });
      setRemarks({ ...remarks, [levelKey]: "" });
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to approve");
      console.error("Error approving:", error);
    } finally {
      setActionLoading({ ...actionLoading, [levelKey]: false });
    }
  };

  const handleReject = async (level: ProjectApprovalLevel) => {
    if (!approvalDoc) return;
    const approvalId = approvalDoc.id || approvalDoc._id;
    if (!approvalId) return;

    const levelKey = `level_${level.level}`;
    if (!remarks[levelKey]?.trim()) {
      toast.error("Remarks are required for rejection");
      return;
    }

    setActionLoading({ ...actionLoading, [levelKey]: true });
    try {
      await rejectProject(approvalId, level.level, remarks[levelKey]);
      toast.success("Project rejected successfully");
      fetchApprovals();
      onApprovalChange?.();
      setShowRemarksInput({ ...showRemarksInput, [levelKey]: false });
      setRemarks({ ...remarks, [levelKey]: "" });
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to reject");
      console.error("Error rejecting:", error);
    } finally {
      setActionLoading({ ...actionLoading, [levelKey]: false });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case "Rejected":
        return <XCircle className="w-6 h-6 text-red-600" />;
      case "Pending":
        return <Clock className="w-6 h-6 text-yellow-600" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800 border-green-300";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-300";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if current user can act on this level
  const canUserActOnLevel = (level: ProjectApprovalLevel): boolean => {
    if (!user) return false;
    const userId = user.id;
    const levelUserId = level.userId;
    return levelUserId === userId && level.status === "Pending";
  };

  // Check if this level can be acted upon (sequential enforcement)
  const canActOnLevel = (level: ProjectApprovalLevel, allLevels: ProjectApprovalLevel[]): boolean => {
    if (level.status !== "Pending") return false;
    
    // First level can always be acted upon if pending
    if (level.level === 1) return true;
    
    // Check if all previous levels are approved
    const previousLevels = allLevels.filter((l) => l.level < level.level);
    const allPreviousApproved = previousLevels.every((l) => l.status === "Approved");
    
    return allPreviousApproved;
  };

  const approvalLevels = approvalDoc?.approvalLevels || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col">
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Approval Timeline - {project.name}</h2>
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Project Details</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Type:</span> <span className="font-medium">{project.type}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>{" "}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status || "Draft")}`}>
                  {project.status || "Draft"}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Estimated Cost:</span>{" "}
                <span className="font-medium">
                  {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(project.estimatedCost || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Project Lead:</span>{" "}
                <span className="font-medium">{project.projectLeadName || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 mt-6 mb-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Approval Workflow</h3>
            {approvalLevels.some((a) => a.status === "Pending") && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Note:</span> Only assigned users can approve their respective levels
                </p>
              </div>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading approvals...</p>
            </div>
          ) : !approvalDoc ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No approval workflow found for this project</p>
              <p className="text-sm text-gray-500 mt-2">Please submit the project for approval first</p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvalLevels.map((level) => {
                const levelKey = `level_${level.level}`;
                const isPending = level.status === "Pending";
                const canUserAct = canUserActOnLevel(level);
                const canAct = canActOnLevel(level, approvalLevels);
                const showActions = canUserAct && canAct;

                return (
                  <div
                    key={level.level}
                    className={`border-l-4 ${
                      level.status === "Approved"
                        ? "border-green-500"
                        : level.status === "Rejected"
                        ? "border-red-500"
                        : "border-yellow-500"
                    } pl-4 py-3 bg-white rounded-r-lg shadow-sm`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1">{getStatusIcon(level.status)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-800">
                              Level {level.level}: {level.roleName}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(level.status)}`}>
                              {level.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            Assigned to: <span className="font-medium">{level.userName}</span>
                          </p>
                          {level.approverName && (
                            <p className="text-sm text-gray-600">
                              {level.status === "Approved" ? "Approved" : "Rejected"} by:{" "}
                              <span className="font-medium">{level.approverName}</span>
                            </p>
                          )}
                          {level.approvedAt && (
                            <p className="text-xs text-gray-500">On: {formatDate(level.approvedAt)}</p>
                          )}
                          {level.remarks && (
                            <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-2 rounded">
                              <span className="font-medium">Remarks:</span> {level.remarks}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {showActions && (
                      <div className="mt-3 ml-9 space-y-2 border-t pt-3 border-gray-200">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Action Required:</p>
                        {!showRemarksInput[levelKey] && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowRemarksInput({ ...showRemarksInput, [levelKey]: true })}
                              className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm transition-colors"
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => setShowRemarksInput({ ...showRemarksInput, [levelKey]: true })}
                              className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 shadow-sm transition-colors"
                            >
                              ✗ Reject
                            </button>
                          </div>
                        )}
                        {showRemarksInput[levelKey] && (
                          <div className="space-y-2">
                            <textarea
                              value={remarks[levelKey] || ""}
                              onChange={(e) => setRemarks({ ...remarks, [levelKey]: e.target.value })}
                              placeholder="Enter remarks (required for rejection)"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(level)}
                                disabled={actionLoading[levelKey]}
                                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 shadow-sm transition-colors"
                              >
                                {actionLoading[levelKey] ? "Processing..." : "✓ Submit Approval"}
                              </button>
                              <button
                                onClick={() => handleReject(level)}
                                disabled={actionLoading[levelKey] || !remarks[levelKey]?.trim()}
                                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 shadow-sm transition-colors"
                              >
                                {actionLoading[levelKey] ? "Processing..." : "✗ Submit Rejection"}
                              </button>
                              <button
                                onClick={() => {
                                  setShowRemarksInput({ ...showRemarksInput, [levelKey]: false });
                                  setRemarks({ ...remarks, [levelKey]: "" });
                                }}
                                className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ApprovalTimeline;
